import ProductsApi from "@/scripts/api/products.api";
import { AbSelectChangeDetail, AbSelectElement, AbSelectOption, AbSelectSearchDetail } from "@/scripts/components/ab-select/ab-select.types";
import { filterOptionsByTerm } from "@/scripts/utils/filterProductByNames.util";
import { ProductNameResponseItemDTO, ProductNamesQueryDTO, ProductResponseDTO } from "@shared/DTO";
import { Mount } from "@shared/enums";
import { getMountDefinition, isMount } from "@shared/values";
import { getCompatibleMountsForSelection, LensCompatibilitySelection, selectListingCamera, selectListingMounts } from "./listingMountCompatibility";
import { CameraFilterConfig, readCameraFilterConfig } from "./cameraFilterConfig";
import { createFilterDescription } from "./filterDescription";
import { parseProductFilterOpen } from "../abProducts.config";

export interface CameraFilterRepository {
  getProductNames(filters: ProductNamesQueryDTO): Promise<ProductNameResponseItemDTO[]>;
  getProduct(id: string): Promise<ProductResponseDTO>;
}

// ab-select renders option text as HTML; escape names from the product API at this boundary.
const escapeOptionText = (value: string): string => value.replace(/[&<>"']/g, (character) => ({
  "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
})[character]!);

/** A listing filter; its mount policy is intentionally separate from ab-lens-compatibility. */
export class AbFilterLensCompatibilityElement extends HTMLElement {
  cameraRepository: CameraFilterRepository = ProductsApi;
  #availableMounts: readonly Mount[] = [];
  #selection: LensCompatibilitySelection = { mode: "none" };
  #config?: CameraFilterConfig;
  #mountOptionsEl?: HTMLElement;
  #cameraArea?: HTMLElement;
  #cameraSelect?: AbSelectElement;
  #cameraOptions: AbSelectOption[] = [];
  #cameraProducts: ProductNameResponseItemDTO[] = [];
  #selectedCamera?: HTMLElement;
  #selectedCameraName?: HTMLElement;
  #clearCameraButton?: HTMLButtonElement;
  #errorEl?: HTMLElement;
  #cameraRequest = 0;
  #cameraLoadToken = 0;
  #camerasLoaded = false;
  #cameraSearchBound = false;

  connectedCallback() {
    if (this.#mountOptionsEl) {
      if (this.#config?.enabled && !this.#camerasLoaded) void this.#loadCameras();
      return;
    }
    this.#config = readCameraFilterConfig(this);
    const details = document.createElement("details");
    details.open = parseProductFilterOpen(this);
    const title = document.createElement("summary");
    title.textContent = this.getAttribute("label") || (this.#config.enabled ? "Compatibilità" : "Attacco della fotocamera");
    this.#mountOptionsEl = document.createElement("div");
    this.#mountOptionsEl.className = "ab-filter-lens-compatibility__mounts";
    details.append(title);
    const description = createFilterDescription(this);
    if (description) details.append(description);
    details.append(this.#mountOptionsEl);
    if (this.#config.enabled) {
      this.#cameraArea = document.createElement("div");
      this.#cameraArea.className = "ab-filter-lens-compatibility__camera";
      this.#cameraSelect = document.createElement("ab-select") as AbSelectElement;
      this.#cameraSelect.setAttribute("dropdown", "");
      this.#cameraSelect.setAttribute("font-size", "sm");
      this.#cameraSelect.addEventListener("ab-select-change", (event) => {
        const option = (event as CustomEvent<AbSelectChangeDetail>).detail?.option;
        if (option?.id) void this.#selectCamera(option);
      });
      this.#selectedCamera = document.createElement("div");
      this.#selectedCamera.className = "ab-filter-lens-compatibility__selected-camera";
      this.#selectedCameraName = document.createElement("span");
      this.#selectedCameraName.className = "ab-filter-lens-compatibility__selected-camera-name";
      this.#clearCameraButton = document.createElement("button");
      this.#clearCameraButton.type = "button";
      this.#clearCameraButton.className = "ab-filter-lens-compatibility__clear-camera";
      this.#clearCameraButton.textContent = "×";
      this.#clearCameraButton.addEventListener("click", () => this.#clearCameraSelection());
      this.#selectedCamera.append(this.#selectedCameraName, this.#clearCameraButton);
      this.#errorEl = document.createElement("p");
      this.#errorEl.setAttribute("role", "alert");
      this.#cameraArea.append(this.#cameraSelect, this.#selectedCamera, this.#errorEl);
      details.appendChild(this.#cameraArea);
      void this.#loadCameras();
    }
    this.replaceChildren(details);
    const cameraInput = this.#cameraSelect?.querySelector<HTMLInputElement>(".ab-select__input");
    if (cameraInput) {
      cameraInput.placeholder = "Scegli fotocamera";
      cameraInput.setAttribute("aria-label", "Scegli fotocamera");
    }
    this.#renderMounts();
    this.#renderCamera();
  }

  update(availableMounts: readonly Mount[]) {
    this.#availableMounts = availableMounts;
    this.#renderMounts();
  }

  resetSelection(emit = false) {
    this.#cameraRequest++;
    this.#selection = { mode: "none" };
    this.#cameraSelect?.clear();
    if (this.#cameraSelect) this.#cameraSelect.options = this.#cameraOptions;
    if (this.#errorEl) this.#errorEl.textContent = "";
    this.#renderMounts();
    this.#renderCamera();
    if (emit) this.#emit();
  }

  #clearCameraSelection() {
    this.#cameraRequest++;
    this.#selection = { mode: "none" };
    this.#cameraSelect?.clear();
    if (this.#cameraSelect) this.#cameraSelect.options = this.#cameraOptions;
    if (this.#errorEl) this.#errorEl.textContent = "";
    this.#renderCamera();
    this.#emit();
    this.#cameraSelect?.querySelector<HTMLInputElement>(".ab-select__input")?.focus();
  }

  #renderMounts() {
    if (!this.#mountOptionsEl) return;
    const current = Array.from(this.#mountOptionsEl.querySelectorAll<HTMLInputElement>("input")).map((input) => input.value);
    if (current.join("\0") !== this.#availableMounts.join("\0")) {
      this.#mountOptionsEl.replaceChildren(...this.#availableMounts.map((mount) => {
        const label = document.createElement("label");
        const input = document.createElement("input");
        input.type = "checkbox";
        input.value = mount;
        input.addEventListener("change", () => {
          this.#cameraRequest++;
          this.#cameraSelect?.clear();
          if (this.#cameraSelect) this.#cameraSelect.options = this.#cameraOptions;
          this.#selection = selectListingMounts(Array.from(this.#mountOptionsEl!.querySelectorAll<HTMLInputElement>("input:checked"))
            .map(({ value }) => value).filter(isMount));
          this.#renderCamera();
          this.#emit();
        });
        label.append(input, ` ${getMountDefinition(mount)?.name ?? mount}`);
        return label;
      }));
    }
    this.#mountOptionsEl.querySelectorAll<HTMLInputElement>("input").forEach((input) => {
      input.checked = this.#selection.mode === "mounts" && this.#selection.mounts.includes(input.value as Mount);
    });
  }

  async #loadCameras() {
    const config = this.#config!;
    const token = ++this.#cameraLoadToken;
    try {
      const cameras = await this.cameraRepository.getProductNames({
        type: config.types,
        brand: config.brands,
        visibility: ["showInCompatibilityTools"],
      });
      if (!this.isConnected || token !== this.#cameraLoadToken) return;
      const options: AbSelectOption[] = cameras.map(({ _id, productData }) => ({
        id: _id,
        label: escapeOptionText(productData.names?.[0] ?? ""),
        group: escapeOptionText(productData.brand),
      }));
      this.#cameraProducts = cameras;
      this.#cameraOptions = options;
      this.#cameraSelect!.options = options;
      if (!this.#cameraSearchBound) {
        this.#cameraSelect!.addEventListener("ab-select-search", (event) => {
          const term = (event as CustomEvent<AbSelectSearchDetail>).detail?.term ?? "";
          this.#cameraSelect!.options = filterOptionsByTerm(term, this.#cameraOptions, this.#cameraProducts);
        });
        this.#cameraSearchBound = true;
      }
      this.#camerasLoaded = true;
    } catch {
      if (token === this.#cameraLoadToken && this.isConnected && this.#errorEl) {
        this.#errorEl.textContent = "Impossibile caricare le fotocamere.";
      }
    }
  }

  async #selectCamera(option: AbSelectOption) {
    const request = ++this.#cameraRequest;
    if (this.#errorEl) this.#errorEl.textContent = "";
    try {
      const camera = await this.cameraRepository.getProduct(option.id);
      if (request !== this.#cameraRequest) return;
      const mount = camera.cameraInterchangeableLensData?.mount;
      if (!isMount(mount)) throw new Error("Camera has no valid mount");
      this.#selection = selectListingCamera({
        id: option.id,
        name: camera.productData?.names?.[0] ?? option.id,
        mount,
      });
      this.#renderMounts();
      this.#renderCamera();
      this.#emit();
    } catch {
      if (request === this.#cameraRequest && this.#errorEl) {
        this.#errorEl.textContent = "Impossibile leggere l'attacco della fotocamera.";
      }
    }
  }

  #renderCamera() {
    if (!this.#cameraArea) return;
    const selected = this.#selection.mode === "camera";
    this.#cameraSelect!.hidden = selected;
    this.#selectedCamera!.hidden = !selected;
    this.#selectedCameraName!.textContent = selected ? this.#selection.camera.name : "";
    if (selected) this.#clearCameraButton!.setAttribute("aria-label", `Rimuovi ${this.#selection.camera.name}`);
  }

  #emit() {
    this.dispatchEvent(new CustomEvent<readonly Mount[]>("lens-compatibility-change", {
      detail: getCompatibleMountsForSelection(this.#selection), bubbles: true,
    }));
  }
}

export const defineAbFilterLensCompatibilityElement = () => {
  if (!customElements.get("ab-filter-lens-compatibility")) {
    customElements.define("ab-filter-lens-compatibility", AbFilterLensCompatibilityElement);
  }
};
